import React from 'react'
import styled from 'styled-components'
import { Panel } from '../elements'
import GameCard from 'components/games/GameCard'
import { Button, PrimaryButton } from 'shared/Buttons'
import { HTTP_URL } from 'config'
import useCopyToClipboard from 'app/useCopyToClipboard'
import TooltipTrigger from 'shared/TooltipTrigger'
import GameContainer from '../GameContainer'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Stack from 'shared/content-control/Stack'
import { Robot, Gemini } from 'app/icons'
import EvenlySpaced from 'shared/content-control/EvenlySpaced'
import { useAddABot, useStartGame } from '../queries'
import { navigate } from '@reach/router'
import * as Menu from 'components/play/PlayGameMenu'
import { MAX_NUMBER_OF_PLAYERS } from 'app/constants'
import Card from 'shared/Card'

const PANELS = {
  DEFAULT: 'DEFAULT',
  ADD_PARTICIPANT: 'ADD_PARTICIPANT',
}

/**
 * Entry point for a game that has been created
 */
const CreatedGame = ({ game }) => {
  const panelState = React.useState(PANELS.DEFAULT)
  return (
    <GameContainer>
      <CreatedGameMenu {...{ panelState, game }} />
      {(() => {
        if (panelState[0] === PANELS.DEFAULT)
          return <DefaultPanel game={game} panelState={panelState} />
        if (panelState[0] === PANELS.ADD_PARTICIPANT)
          return <AddParticipantPanel game={game} panelState={panelState} />
        return <DefaultPanel game={game} panelState={panelState} />
      })()}
      <div style={{ height: '3rem' }}></div>
    </GameContainer>
  )
}

const DefaultPanel = ({ game: { players }, panelState, game }) => {
  const AddPlayerButton = ({ panelState: [_, setPanel] }) => {
    return (
      <PrimaryButton onClick={() => setPanel(PANELS.ADD_PARTICIPANT)}>
        Add a player
      </PrimaryButton>
    )
  }

  return (
    <Panel centered style={{ minHeight: '23rem' }}>
      <Stack space={3}>
        <h1>Game Open</h1>
        <GameCard
          game={game}
          hideActions
          style={{ margin: 'auto', textAlign: 'left' }}
        />
        {game.players.length < 2 ? (
          <Stack space={3}>
            <div>
              {players.length < 2
                ? `Add 1 more player to start the game`
                : `Add more players or start the game`}
            </div>
            <AddPlayerButton panelState={panelState} />
          </Stack>
        ) : (
          <Stack space={3}>
            <StartGameButton game={game} />
            {players.length < MAX_NUMBER_OF_PLAYERS && (
              <CopyLinkAndShareButton game={game} isPrimary={false} />
            )}
          </Stack>
        )}
      </Stack>
    </Panel>
  )
}

const CreatedGameMenu = ({ panelState, game: { players } }) => {
  const [panel, setPanel] = panelState

  const onBack =
    panel === PANELS.DEFAULT
      ? () => navigate('/')
      : () => setPanel(PANELS.DEFAULT)
  return (
    <Menu.Wrapper>
      <Menu.BackMenuItem onClick={onBack} />
      {players.length > 1 && <Menu.ChatMenuItem />}
    </Menu.Wrapper>
  )
}

const StartGameButton = ({ game: { id } }) => {
  const [startGame] = useStartGame({ variables: { id } })
  return <PrimaryButton onClick={startGame}>Start game now!</PrimaryButton>
}

const AddParticipantPanel = ({ game, game: { id, players }, panelState }) => {
  const [isPlayWithBotSelected, setIsPlayWithBotSelected] = React.useState(true)

  return (
    <Panel centered style={{ minHeight: '20rem' }}>
      <Stack>
        <h1>Add a Player</h1>
        <ButtonGroup aria-label="Adversaries" className="mb-3">
          <Button
            isPrimary={isPlayWithBotSelected}
            onClick={() => setIsPlayWithBotSelected(true)}
          >
            Play with a bot
          </Button>
          <Button
            isPrimary={!isPlayWithBotSelected && isPlayWithBotSelected != null}
            onClick={() => setIsPlayWithBotSelected(false)}
          >
            Play with friends
          </Button>
        </ButtonGroup>
        {(() => {
          if (isPlayWithBotSelected == null) return <SelectAnOption />
          if (isPlayWithBotSelected === true)
            return <AddABot game={game} panelState={panelState} />
          if (isPlayWithBotSelected === false) return <AddFriends game={game} />
        })()}
      </Stack>
    </Panel>
  )
}

const AddFriends = ({ game }) => (
  <Stack space={3}>
    <Card style={{ textAlign: 'left' }} className={'p-3'}>
      <ul className={'p-0 m-0 ml-3'}>
        <li>Share the link with friends</li>
        <li>Go back and wait for them to join</li>
      </ul>
    </Card>
    <CopyLinkAndShareButton game={game} />
  </Stack>
)

const SelectAnOption = () => (
  <Stack space={3}>
    <div>Pick an option</div>
  </Stack>
)

const RANDOBOT_USER = {
  id: '6318b28aea4bd4003c0b7e91',
  username: 'randobot',
}

const SUPERBOT_USER = {
  id: '68fb59f453747902229f23f6',
  username: 'superbot',
}

const AddABot = ({ game: { id }, panelState }) => {
  const [addABot] = useAddABot()

  const handleAddRandobotOnClick = async () => {
    await addABot({
      variables: { id, bot: RANDOBOT_USER.id },
    })
    panelState[1](PANELS.DEFAULT)
  }

  const handleAddSuperbotOnClick = async () => {
    await addABot({
      variables: { id, bot: SUPERBOT_USER.id },
    })
    panelState[1](PANELS.DEFAULT)
  }

  return (
    <Stack space={3}>
      <div style={{ marginTop: '0.5rem' }}>randobot picks cards at random</div>
      <IconButtonWrapper onClick={handleAddRandobotOnClick}>
        <IconWrapper>
          <Robot />
        </IconWrapper>
        play with randobot
      </IconButtonWrapper>
      <div style={{ marginTop: '0.5rem' }}>
        superbot is powered by Google Gemini LLM
      </div>
      <IconButtonWrapper onClick={handleAddSuperbotOnClick}>
        <IconWrapper>
          <Gemini />
        </IconWrapper>
        play with superbot
      </IconButtonWrapper>
    </Stack>
  )
}

const IconButtonWrapper = ({ onClick, children }) => (
  <Button onClick={onClick} style={{ width: '15rem', margin: '0 auto' }}>
    {children}
  </Button>
)

const CopyLinkAndShareButton = ({ game: { id }, isPrimary = true }) => {
  const [copy] = useCopyToClipboard(`${HTTP_URL}/play/${id}`)

  return (
    <TooltipTrigger text="Link copied!" placement={'bottom'}>
      <Button onClick={copy} mt={'3'} isPrimary={isPrimary}>
        Copy link and invite friends
      </Button>
    </TooltipTrigger>
  )
}

const IconWrapper = styled(EvenlySpaced.Row)`
  height: 3rem;
  margin: 1rem 0;
`

export default CreatedGame
